import { isPrivateNotesActivity } from "./privateNotesScope";
export { isPrivateNotesActivity } from "./privateNotesScope";
export const JAB_ORG_ID = "332a9d47-6e36-11e8-a392-061714f61e3e";
export const INTERNAL_NOTES_ITEM_ID = "labelInternalNotes";
const SOURCE_KEY = "internalNotesSource";

export const isInternalNotesItemId = id => typeof id === "string"
	&& (id === INTERNAL_NOTES_ITEM_ID || id.startsWith(`${INTERNAL_NOTES_ITEM_ID}_`));

export function getInternalNotesAccess(orgId, { administrator = false, internalStaff = false } = {}, activity) {
	return isPrivateNotesActivity(activity, orgId)
		? { canRead: administrator || internalStaff, canEdit: administrator }
		: null;
}

// The private note editors of staff pricing views (job-level Internal Notes, internal SKU notes and comments) are a
// role decision for the JAB organization on any of its activities: administrators edit, designated internal staff
// read, everyone else has no access. The service authorizes each read and write on its own.
export function getPrivateNotesEditorAccess(orgId, { administrator = false, internalStaff = false } = {}) {
	return orgId === JAB_ORG_ID ? { canRead: administrator || internalStaff, canEdit: administrator } : null;
}

// Compatibility entry point for pricing load. Private hydration happens only in
// the authenticated service. The legacy public Parts value stays on the job as
// read-only "Internal Notes (legacy)" until staff retire it.
export function hydrateJabInternalNotes(activity = {}, jobDetails = {}, orgId) {
	if (!isPrivateNotesActivity(activity, orgId)) return jobDetails;
	if (!jobDetails || jobDetails.internalNotesSource === undefined) return jobDetails;
	const { internalNotesSource, ...publicDetails } = jobDetails;
	return publicDetails;
}

// Private edits use the authenticated service. Generic pricing messages cannot
// set or restore private text into canonical/public job details, and the legacy
// Parts value is read-only: the saved value is kept, an incoming one is ignored.
export function mergeJabInternalNotesUpdate(existing = {}, resolved = {}, incoming, access) {
	if (!access) return resolved;
	const { parts, internalNotesSource, ...publicDetails } = resolved || {};
	const savedParts = existing && typeof existing.parts === "string" ? existing.parts : "";
	return savedParts ? { ...publicDetails, parts: savedParts } : publicDetails;
}

// Remove private data before crossing a customer/report boundary, including
// saved estimate/approval snapshots and the original activity answer. Do not
// remove unrelated product/component properties named "parts". Job-detail Parts
// was the public home of internal notes before the private service, so it leaves
// customer and print payloads on every JAB activity, mapped or not.
export function projectJabCustomerData(value, orgId, activity = value) {
	if (orgId !== JAB_ORG_ID) return value;
	if (Array.isArray(value) && activity === value) {
		const projected = value.map(entry => projectJabCustomerData(entry, orgId));
		return projected.every((entry, index) => entry === value[index]) ? value : projected;
	}
	return isPrivateNotesActivity(activity, orgId) ? projectPrivateData(value) : projectLegacyParts(value);
}
function projectLegacyParts(value, parentKey = "") {
	if (Array.isArray(value)) {
		const projected = value.map(entry => projectLegacyParts(entry, parentKey));
		return projected.every((entry, index) => entry === value[index]) ? value : projected;
	}
	if (!value || typeof value !== "object" || value instanceof Date) {
		return value;
	}
	let changed = false;
	const result = Object.keys(value).reduce((out, key) => {
		if (key === SOURCE_KEY || (parentKey === "jobDetails" && key === "parts")) {
			changed = true;
			return out;
		}
		const projected = projectLegacyParts(value[key], key);
		if (projected !== value[key]) {
			changed = true;
		}
		out[key] = projected;
		return out;
	}, {});
	return changed ? result : value;
}
function projectPrivateData(value, parentKey = "") {
	if (Array.isArray(value)) return value.map(entry => projectPrivateData(entry, parentKey)).filter(entry => entry !== undefined);
	if (!value || typeof value !== "object" || value instanceof Date) return value;
	if (isInternalNotesItemId(value.id)) return undefined;
	return Object.keys(value).reduce((result, key) => {
		if (key === SOURCE_KEY || isInternalNotesItemId(key) || (parentKey === "jobDetails" && key === "parts")) return result;
		const projected = projectPrivateData(value[key], key);
		if (projected !== undefined) result[key] = projected;
		return result;
	}, {});
}

// Compatibility export for older callers; do not restore private canonical data.
export function preserveJabInternalNotes(incoming, activity = incoming) {
	return projectJabCustomerData(incoming, JAB_ORG_ID, activity);
}
